import Hero from "@/components/Hero";
import FinalCTA from "@/components/FinalCTA";
import { Link } from "react-router-dom";
import {
  Building2, Users, Factory, Boxes, Globe, BadgeCheck,
  Cog, Lightbulb, ShieldCheck, Check, Pencil, Hammer, PackageCheck, Truck, ChevronRight, ArrowRight
} from "lucide-react";

const stats = [
  { icon: Building2, value: "10+", label: "Anni di esperienza" },
  { icon: Users, value: "500+", label: "Clienti soddisfatti" },
  { icon: Factory, value: "10.000+ m²", label: "Area produttiva" },
  { icon: Boxes, value: "50M+", label: "Pezzi prodotti all'anno" },
  { icon: Globe, value: "30+", label: "Paesi serviti" },
  { icon: BadgeCheck, value: "ISO 9001", label: "Certificato" },
];

const cards = [
  {
    icon: Cog,
    title: "Fibre",
    body: "Offriamo servizi completi che comprendono la progettazione del prodotto, lo sviluppo degli stampi, la produzione e l'assemblaggio.",
  },
  {
    icon: Lightbulb,
    title: "Soluzioni Complete",
    body: "Offriamo soluzioni complete per la progettazione, la produzione e l'assistenza di prodotti in plastica e ferramenta.",
  },
];

const reasons = [
  "Produzione professionale con vasta esperienza",
  "Soluzioni di personalizzazione complete",
  "Prezzi competitivi",
  "Elevati standard qualitativi",
];

const advantages = [
  { icon: Pencil, label: "Progettazione" },
  { icon: Cog, label: "Sviluppo stampi" },
  { icon: Factory, label: "Produzione" },
  { icon: Hammer, label: "Assemblaggio" },
  { icon: Truck, label: "Spedizione" },
];

export default function Home() {
  return (
    <>
      <Hero
        testId="home-hero"
        fullHeight
        desktopImg="/images/hero/HomeHERODesktop.png"
        mobileImg="/images/hero/HomeHEROMobile.png"
        eyebrow="DROSUN ITALIA"
        title="Il tuo partner affidabile per soluzioni complete"
        titleAccent="di produzione"
        subtitle="Trasformiamo le vostre idee in prodotti di successo attraverso progettazione, sviluppo stampi, produzione e assemblaggio industriale."
        ctaLabel="Scopri di più"
        ctaTo="/servizi"
        secondaryLabel="Contattaci"
        secondaryTo="/contatti"
      />

      {/* Stats strip */}
      <section className="bg-drosun-navy text-white" data-testid="home-stats">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 py-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-10 gap-x-6">
          {stats.map((s, i) => (
            <div key={i} className="flex flex-col items-center text-center px-2 relative">
              <s.icon size={32} className="text-white/90 mb-3" strokeWidth={1.4} />
              <div className="font-display text-2xl md:text-3xl">{s.value}</div>
              <div className="text-xs md:text-sm text-white/70 mt-1">{s.label}</div>
              {i < stats.length - 1 && <span className="hidden lg:block absolute right-0 top-2 bottom-2 w-px bg-white/15" />}
            </div>
          ))}
        </div>
      </section>

      {/* 3 cards */}
      <section className="py-20 bg-white" data-testid="home-cards">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { ...cards[0], img: "/images/home/fibre.png" },
            { ...cards[1], img: "/images/home/soluzioni.png" },
          ].map((c, i) => (
            <article key={i} className="reveal rounded-2xl bg-drosun-light overflow-hidden border border-neutral-200/70 shadow-[0_10px_40px_-22px_rgba(0,0,0,0.25)]">
              <div className="relative h-56 bg-drosun-navy">
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute -bottom-7 left-6 w-14 h-14 rounded-full bg-drosun-navy text-white inline-flex items-center justify-center shadow-lg">
                  <c.icon size={22} />
                </span>
              </div>
              <div className="p-7 pt-10">
                <h3 className="font-display text-2xl uppercase tracking-wide text-drosun-text">{c.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-drosun-muted">{c.body}</p>
              </div>
            </article>
          ))}

          {/* Perché scegliere noi */}
          <article className="reveal rounded-2xl bg-drosun-light overflow-hidden border border-neutral-200/70 shadow-[0_10px_40px_-22px_rgba(0,0,0,0.25)]">
            <div className="relative h-56 bg-drosun-navy">
              <img src="/images/home/perchenoi.png" alt="Perché scegliere noi" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <span className="absolute -bottom-7 left-6 w-14 h-14 rounded-full bg-drosun-navy text-white inline-flex items-center justify-center shadow-lg">
                <ShieldCheck size={22} />
              </span>
            </div>
            <div className="p-7 pt-10">
              <h3 className="font-display text-2xl uppercase tracking-wide text-drosun-text">Perché scegliere noi?</h3>
              <ul className="mt-4 space-y-3">
                {reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-drosun-text">
                    <Check size={20} className="text-drosun-red shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* Chi è Drosun (dark) */}
      <section className="relative bg-drosun-steel text-white py-20" data-testid="home-chi-siamo">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="inline-block w-10 h-[3px] bg-drosun-red" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/70">CHI È DROSUN ITALIA</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl leading-tight">DROSUN</h2>
            <p className="mt-6 text-[17px] leading-relaxed text-white/85">
              <strong>Drosun Italia</strong> è un fornitore professionale specializzato nella <strong>progettazione</strong>, sviluppo e <strong>produzione</strong> di prodotti personalizzati.
              Vantiamo esperienza nella progettazione e realizzazione di <strong>stampi</strong>, nella produzione di articoli in <strong>plastica</strong> e <strong>metallo</strong>, nell'assemblaggio e nella gestione delle spedizioni.
            </p>
            <p className="mt-4 text-[16px] leading-relaxed text-white/75">
              Grazie a tecnologie avanzate, attrezzature di precisione e un rigoroso sistema di gestione della qualità, offriamo prodotti e servizi efficienti per trasformare le vostre idee in realtà.
            </p>
            <Link to="/servizi" className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-drosun-red hover:bg-drosun-redHover text-white text-sm font-bold uppercase tracking-[0.12em] rounded-md transition-colors" data-testid="home-chi-siamo-cta">
              I nostri servizi <ArrowRight size={18} />
            </Link>
          </div>
          <div className="reveal relative">
            <div className="absolute -inset-4 rounded-2xl bg-drosun-red/10 blur-2xl" aria-hidden />
            <img src="/images/home/chiedrosun.png" alt="Chi è Drosun Italia" className="relative w-full h-[420px] object-cover rounded-2xl shadow-2xl" loading="lazy" />
          </div>
        </div>
      </section>

      {/* I nostri vantaggi */}
      <section className="py-20 bg-white" data-testid="home-vantaggi">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text">I nostri vantaggi</h2>
          <div className="mt-2 mx-auto w-16 h-[3px] bg-drosun-red" />
          <p className="mt-6 max-w-3xl mx-auto text-[17px] text-drosun-muted">
            Un servizio completo per la <strong>progettazione</strong>, lo sviluppo, la realizzazione di <strong>stampi</strong>, la <strong>produzione</strong>, l'<strong>assemblaggio</strong> e la spedizione di prodotti personalizzati.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 md:gap-6">
            {advantages.map((a, i) => (
              <div key={i} className="contents">
                <div className="flex flex-col items-center w-[140px]">
                  <span className="w-20 h-20 rounded-full bg-drosun-light border border-neutral-200 inline-flex items-center justify-center text-drosun-text shadow-sm">
                    <a.icon size={30} strokeWidth={1.5} />
                  </span>
                  <span className="mt-3 text-sm font-semibold text-drosun-text">{a.label}</span>
                </div>
                {i < advantages.length - 1 && (
                  <span className="dot-red" aria-hidden><ChevronRight size={16} /></span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA testId="home-final-cta" />
    </>
  );
}
